import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { t } from '../../i18n';

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * <cadence-photo-upload endpoint="/portal/profile/photo" name="Jane Doe" src="...">
 * Shows the current photo (or initials) with an Upload button. On selection it
 * validates, previews locally, POSTs the raw file to `endpoint`, and emits
 * `photo-uploaded` with `{ url }` on success.
 */
@customElement('cadence-photo-upload')
export class CadencePhotoUpload extends LitElement {
  @property() endpoint = '';
  @property() name = '';
  @property() src = '';
  @property({ type: Number }) size = 120;

  @state() private preview = '';
  @state() private uploading = false;
  @state() private error = '';

  static styles = css`
    :host { display: inline-block; }
    .wrap { display: flex; flex-direction: column; align-items: center; gap: 10px; }
    input[type=file] { display: none; }
    .error { color: var(--color-danger); font-size: 0.78rem; text-align: center; max-width: 180px; }
    .hint { color: var(--color-text-secondary); font-size: 0.72rem; }
  `;

  private get fileInput(): HTMLInputElement | null {
    return this.renderRoot.querySelector('input[type=file]');
  }

  private pick() { this.fileInput?.click(); }

  private async onFile(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.error = '';
    if (!ALLOWED.includes(file.type)) { this.error = t('ui.photoUpload.badType'); return; }
    if (file.size > MAX_BYTES) { this.error = t('ui.photoUpload.tooLarge'); return; }

    // Local preview while the upload runs.
    this.preview = URL.createObjectURL(file);
    this.uploading = true;
    try {
      const { url } = await api.upload<{ url: string }>(this.endpoint, file);
      this.src = url;
      this.dispatchEvent(new CustomEvent('photo-uploaded', { detail: { url }, bubbles: true, composed: true }));
    } catch (err) {
      this.preview = '';
      this.error = err instanceof ApiError ? err.message : t('ui.photoUpload.failed');
    } finally {
      this.uploading = false;
      if (this.fileInput) this.fileInput.value = '';
    }
  }

  render() {
    const shown = this.preview || this.src;
    return html`
      <div class="wrap">
        <cadence-avatar name=${this.name} src=${shown} size=${this.size}></cadence-avatar>
        <cadence-button variant="secondary" ?loading=${this.uploading} @click=${() => this.pick()}>
          ${this.uploading ? t('ui.photoUpload.uploading') : shown ? t('ui.photoUpload.change') : t('ui.photoUpload.upload')}
        </cadence-button>
        <span class="hint">${t('ui.photoUpload.hint')}</span>
        ${this.error ? html`<span class="error" role="alert">${this.error}</span>` : ''}
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change=${this.onFile} />
      </div>
    `;
  }
}
